import { create } from 'zustand';
import apiClient from '../lib/api';

export interface WorkNote {
    id: string;
    tenantId: string;
    title: string;
    content: string;
    isPinned: boolean;
    tags: string[];
    createdAt: string;
    updatedAt: string;
}

interface NoteState {
    notes: WorkNote[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchNotes: (filters?: any) => Promise<void>;
    createNote: (data: Partial<WorkNote>) => Promise<void>;
    updateNote: (id: string, data: Partial<WorkNote>) => Promise<void>;
    deleteNote: (id: string) => Promise<void>;
}

export const useWorkNoteStore = create<NoteState>((set, get) => ({
    notes: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
    },
    fetchNotes: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/work/notes', { params: filters });
            const { data, pagination } = response.data;
            set({ notes: data, pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch notes', isLoading: false });
        }
    },
    createNote: async (data: Partial<WorkNote>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/work/notes', data);
            const newNote = response.data;
            set((state) => ({
                notes: [newNote, ...state.notes].sort((a, b) => {
                    if (a.isPinned && !b.isPinned) return -1;
                    if (!a.isPinned && b.isPinned) return 1;
                    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
                }),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create note', isLoading: false });
            throw error;
        }
    },
    updateNote: async (id: string, data: Partial<WorkNote>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/work/notes/${id}`, data);
            const updatedNote = response.data;
            set((state) => ({
                notes: state.notes.map((o) => (o.id === id ? updatedNote : o)).sort((a, b) => {
                    if (a.isPinned && !b.isPinned) return -1;
                    if (!a.isPinned && b.isPinned) return 1;
                    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
                }),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update note', isLoading: false });
            throw error;
        }
    },
    deleteNote: async (id: string) => {
        set({ error: null });
        try {
            await apiClient.delete(`/work/notes/${id}`);
            set((state) => ({ notes: state.notes.filter((n) => n.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete note' });
            throw error;
        }
    },
}));
